/**
 * Form Utilities
 * 
 * Helper functions for form validation, formatting, and common operations.
 */

/**
 * Email validation
 * Validates email format using a standard regex pattern.
 */
export const validateEmail = (email) => {
  if (!email) return 'Email is required';
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }
  
  return null;
};

/**
 * Password validation
 * Validates password strength based on common requirements.
 */
export const validatePassword = (password, options = {}) => {
  const {
    minLength = 8,
    requireUppercase = true,
    requireLowercase = true,
    requireNumbers = true,
    requireSpecialChars = true,
  } = options;
  
  if (!password) return 'Password is required';
  
  if (password.length < minLength) {
    return `Password must be at least ${minLength} characters long`;
  }
  
  if (requireUppercase && !/[A-Z]/.test(password)) {
    return 'Password must contain at least one uppercase letter';
  }
  
  if (requireLowercase && !/[a-z]/.test(password)) {
    return 'Password must contain at least one lowercase letter';
  }
  
  if (requireNumbers && !/\d/.test(password)) {
    return 'Password must contain at least one number';
  }
  
  if (requireSpecialChars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return 'Password must contain at least one special character';
  }
  
  return null;
};

/**
 * URL validation
 * Validates URL format.
 */
export const validateUrl = (url) => {
  if (!url) return 'URL is required';
  
  try {
    new URL(url);
    return null;
  } catch {
    return 'Please enter a valid URL';
  }
};

/**
 * Required field validation
 * Generic validation for required fields.
 */
export const validateRequired = (value, fieldName = 'This field') => {
  if (!value || (typeof value === 'string' && !value.trim())) {
    return `${fieldName} is required`;
  }
  return null;
};

/**
 * Min length validation
 */
export const validateMinLength = (value, minLength, fieldName = 'This field') => {
  if (value && value.length < minLength) {
    return `${fieldName} must be at least ${minLength} characters`;
  }
  return null;
};

/**
 * Max length validation
 */
export const validateMaxLength = (value, maxLength, fieldName = 'This field') => {
  if (value && value.length > maxLength) {
    return `${fieldName} must be no more than ${maxLength} characters`;
  }
  return null;
};

/**
 * Number validation
 */
export const validateNumber = (value, options = {}) => {
  const { min, max, integer = false } = options;
  
  if (value === '' || value === null || value === undefined) return null;
  
  const num = Number(value);
  
  if (isNaN(num)) {
    return 'Please enter a valid number';
  }
  
  if (integer && !Number.isInteger(num)) {
    return 'Please enter a whole number';
  }
  
  if (min !== undefined && num < min) {
    return `Number must be at least ${min}`;
  }
  
  if (max !== undefined && num > max) {
    return `Number must be no more than ${max}`;
  }
  
  return null;
};

/**
 * Username validation
 * Validates username format (alphanumeric, hyphens, underscores).
 */
export const validateUsername = (username, options = {}) => {
  const { minLength = 3, maxLength = 30 } = options;
  
  if (!username) return 'Username is required';
  
  const usernameRegex = /^[a-zA-Z0-9_-]+$/;
  if (!usernameRegex.test(username)) {
    return 'Username can only contain letters, numbers, hyphens, and underscores';
  }
  
  if (username.length < minLength) {
    return `Username must be at least ${minLength} characters`;
  }
  
  if (username.length > maxLength) {
    return `Username must be no more than ${maxLength} characters`;
  }
  
  return null;
};

/**
 * Phone number validation (basic US format)
 */
export const validatePhone = (phone) => {
  if (!phone) return null; // Optional field
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  if (digits.length !== 10 && digits.length !== 11) {
    return 'Please enter a valid phone number';
  }
  
  return null;
};

/**
 * Format phone number
 * Formats a phone number to (XXX) XXX-XXXX
 */
export const formatPhone = (phone) => {
  const digits = phone.replace(/\D/g, '');
  
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  
  if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  
  return phone;
};

/**
 * Validate form with multiple fields
 * Takes an object of field validators and returns errors.
 * 
 * @example
 * const errors = validateForm({
 *   email: () => validateEmail(email),
 *   password: () => validatePassword(password),
 *   username: () => validateUsername(username),
 * });
 */
export const validateForm = (validators) => {
  const errors = {};
  
  Object.keys(validators).forEach((fieldName) => {
    const error = validators[fieldName]();
    if (error) {
      errors[fieldName] = error;
    }
  });
  
  return errors;
};

/**
 * Check if form has errors
 */
export const hasFormErrors = (errors) => {
  return Object.keys(errors).length > 0;
};

/**
 * Format error message from API response
 * Handles various error response formats from the backend.
 */
export const formatApiError = (error, defaultMessage = 'An error occurred') => {
  if (typeof error === 'string') return error;
  
  if (error && error.message) return error.message;
  if (error && error.error) return error.error;
  
  // Handle Django-style field errors
  if (error && typeof error === 'object') {
    const firstKey = Object.keys(error)[0];
    if (firstKey && Array.isArray(error[firstKey])) {
      return error[firstKey][0];
    }
    if (firstKey && typeof error[firstKey] === 'string') {
      return error[firstKey];
    }
  }
  
  return defaultMessage;
};

/**
 * Get label styling based on state
 */
export const getLabelClasses = (hasError = false, disabled = false) => {
  const baseClasses = 'block text-sm font-medium';
  
  if (disabled) return `${baseClasses} text-gray-400`;
  if (hasError) return `${baseClasses} text-red-700`;
  
  return `${baseClasses} text-gray-700`;
};

/**
 * Debounce function for search inputs and real-time validation
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Check if two values are equal (useful for detecting changes)
 */
export const isEqual = (a, b) => {
  if (a === b) return true;
  
  if (typeof a === 'object' && typeof b === 'object' && a !== null && b !== null) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  
  return false;
};

/**
 * Form submission handler wrapper
 * Prevents default and handles common form submission patterns.
 */
export const handleFormSubmit = (callback) => async (e) => {
  if (e && e.preventDefault) {
    e.preventDefault();
  }
  
  if (e && e.stopPropagation) {
    e.stopPropagation();
  }
  
  return await callback();
};
